import {
	profileSchema,
	routes,
	type InterestsResponse,
	type Profile,
	type TelegramAuthResponse,
	type UpdateMeRequest,
} from '@spot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api';
import { useSession } from '../auth/session-gate';

export function profileQueryKey(userId: string) {
	return ['users', userId] as const;
}

/** S12/S13: `GET /v1/users/:id` (the API hides banned, deleted and blocked users). */
export function useProfile(userId: string) {
	const { api } = useSession();
	return useQuery<Profile>({
		queryKey: profileQueryKey(userId),
		queryFn: () => api.call(routes.userProfile, { params: { id: userId } }),
		retry: (failures, error) => !(error instanceof ApiError && error.status < 500) && failures < 2,
		staleTime: 30_000,
	});
}

export function useInterestOptions() {
	const { api } = useSession();
	return useQuery<InterestsResponse>({
		queryKey: ['meta', 'interests'],
		queryFn: () => api.call(routes.interests),
		staleTime: Infinity,
	});
}

/** S14 writes. Each keeps the own-profile cache and the signed-in user (name) in sync. */
export function useEditProfile() {
	const { api, me } = useSession();
	const queryClient = useQueryClient();
	const key = profileQueryKey(me.id);
	const store = (profile: Profile) => {
		queryClient.setQueryData(key, profile);
	};

	const fields = useMutation({
		mutationFn: (body: UpdateMeRequest) => api.call(routes.updateMe, { body }),
		onSuccess: (updated) => {
			queryClient.setQueryData<TelegramAuthResponse>(['session'], (session) =>
				session === undefined ? session : { ...session, user: updated },
			);
			void queryClient.invalidateQueries({ queryKey: key });
		},
	});
	const interests = useMutation({
		mutationFn: (slugs: string[]) => api.call(routes.setInterests, { body: { slugs } }),
		onSuccess: store,
	});
	const photo = useMutation({
		mutationFn: (image: Blob) => api.upload('/v1/me/photo', image, profileSchema),
		onSuccess: store,
	});
	const removePhoto = useMutation({
		mutationFn: () => api.call(routes.deletePhoto),
		onSuccess: store,
	});
	return { fields, interests, photo, removePhoto };
}
